// Pure display helpers + icon paths for the contact-detail surface (ORB-383). Kept separate from the
// component file so fast-refresh stays happy (component files export only components).

import type { ContactActivityItemDto } from '@/api/contactDetail'

// Strip a trailing brand segment from a page's <title> so the activity chip reads as the page name
// alone: "Top 10 Reasons to Listen to James | OrbitalX" -> "Top 10 Reasons to Listen to James".
// Splits on space-delimited title separators — pipe, en/em dash, middot, ›, and the U+FFFD
// replacement char that appears when a separator glyph fails to encode ("Download � OrbitalX").
// Plain hyphens are left alone so titles like "Top 10 - The Sequel" aren't truncated.
function stripBrandSuffix(title: string): string {
  const first = title.split(/\s+[|–—·»›�]+\s+/)[0].trim()
  return first || title.trim()
}

// Humanize a page path into a readable label as a last resort when a session's pages carry no title:
// ".../webinars/top-10-reasons-to-listen-to-james/index.html" -> "Top 10 Reasons To Listen To James".
function humanizePagePath(path: string): string {
  const segments = path.split('/').filter(Boolean)
  // Drop file + thank-you-style trailing markers to land on the meaningful slug.
  const skip = new Set(['index.html', 'index.htm', 'index', 'thank-you'])
  const slug = [...segments].reverse().find(s => !skip.has(s.toLowerCase()) && !s.includes('.'))
  if (!slug) return ''
  return slug.replace(/[-_]+/g, ' ').replace(/\b\w/g, c => c.toUpperCase()).trim()
}

// Derive a meaningful title for a web session, replacing the redundant server-side "Web session"
// (the Website/Landing-page badge already conveys the kind). Prefers the entry page's cleaned
// <title>, falls back to a humanized entry path, then to the original title (ORB-487).
export function sessionDisplayTitle(item: ContactActivityItemDto): string {
  if (item.Kind !== 'Web' || !item.Pages || item.Pages.length === 0) return item.Title
  // Entry page = earliest by timestamp, independent of the array's order.
  const entry = item.Pages.reduce((earliest, p) =>
    new Date(p.At).getTime() <= new Date(earliest.At).getTime() ? p : earliest
  )
  const title = (entry.Title || '').trim()
  if (title) return stripBrandSuffix(title)
  return humanizePagePath(entry.Path) || item.Title
}

export function relativeTime(iso?: string | null): string {
  if (!iso) return '—'
  const then = new Date(iso).getTime()
  if (Number.isNaN(then)) return '—'
  const sec = Math.round((Date.now() - then) / 1000)
  if (sec < 0) return formatDate(iso)
  if (sec < 60) return 'just now'
  const min = Math.round(sec / 60)
  if (min < 60) return `${min}m ago`
  const hr = Math.round(min / 60)
  if (hr < 24) return `${hr}h ago`
  const day = Math.round(hr / 24)
  if (day < 14) return `${day}d ago`
  const wk = Math.round(day / 7)
  if (wk < 8) return `${wk}w ago`
  return formatDate(iso)
}

export function formatDate(iso?: string | null): string {
  if (!iso) return '—'
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
}

export function formatDateTime(iso?: string | null): string {
  if (!iso) return '—'
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
}

export function formatDuration(seconds?: number | null): string | null {
  if (!seconds || seconds <= 0) return null
  const m = Math.floor(seconds / 60)
  const s = seconds % 60
  return m > 0 ? `${m}m${s ? ` ${s}s` : ''}` : `${s}s`
}

// High -> Hot, Medium -> Warm, Low -> Cool (the API stays neutral; the UI maps to the heat language).

export const ICONS = {
  check: 'M20 6 9 17l-5-5',
  mail: 'M3 5h18v14H3z|m3 7 9 6 9-6',
  sparkle: 'M13 3l2.3 6.2L21 11l-5.7 1.8L13 19l-2.3-6.2L5 11l5.7-1.8L13 3Z',
  chevron: 'm9 6 6 6-6 6',
  arrowRight: 'M5 12h14M13 6l6 6-6 6',
  copy: 'M9 9h11v11H9zM5 15V5a2 2 0 0 1 2-2h10',
  phone: 'M22 16.9v3a2 2 0 0 1-2.2 2 19.8 19.8 0 0 1-8.6-3 19.5 19.5 0 0 1-6-6 19.8 19.8 0 0 1-3-8.6A2 2 0 0 1 4.1 2h3a2 2 0 0 1 2 1.7c.1 1 .3 1.9.6 2.8a2 2 0 0 1-.5 2.1L8.1 9.9a16 16 0 0 0 6 6l1.3-1.3a2 2 0 0 1 2.1-.4c.9.3 1.8.5 2.8.6a2 2 0 0 1 1.7 2Z',
  target: 'M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18M12 7.5a4.5 4.5 0 1 0 0 9 4.5 4.5 0 0 0 0-9',
}
